import {AxiosRequestConfig} from "axios";
import httpResource from "@/components/infrastructures/functions/http/httpResource";
import authService from "@/components/domains/auth/authService";
import store from "@/store";
import {AuthGetterTypes} from "@/store/auth/types";
import {AuthError, toAuthError} from "@/components/domains/auth/authPolicy";
import {
    AssertionCredentialLike,
    AttestationCredentialLike,
    AuthenticationOptionsJson,
    classifyCeremonyError,
    loginBody,
    Passkey,
    registrationBody,
    RegistrationOptionsJson,
    toCreationOptions,
    toPasskey,
    toRequestOptions,
} from "@/components/domains/auth/webauthnJson";

/**
 * "指紋・顔でログイン" (WebAuthn). Backend contract (all 404 while
 * AUTH_PASSKEY_ENABLED is off):
 *
 *   POST   /auth/passkey/login/options        -> AuthenticationOptionsJson
 *   POST   /auth/passkey/login/verify   body  -> 200 + Set-Cookie (then /auth/me)
 *   POST   /passkey/registration/options      -> RegistrationOptionsJson (signed in)
 *   POST   /passkey/registration/verify body  -> 201 Passkey             (signed in)
 *   GET    /passkey/credentials               -> Passkey[]               (signed in)
 *   DELETE /passkey/credentials/{id}          -> 204                     (signed in)
 *
 * Every call bypasses the global interceptor (`handlerEnabled: false`) so a
 * 401/403 from these endpoints neither logs the admin out nor redirects; the
 * screens show the message inline.
 */

export const CEREMONY_CANCELLED = "CEREMONY_CANCELLED";
export const CEREMONY_ALREADY_REGISTERED = "CEREMONY_ALREADY_REGISTERED";
export const CEREMONY_UNSUPPORTED = "CEREMONY_UNSUPPORTED";

export const PASSKEY_MESSAGES = {
    cancelled: "指紋・顔での確認ができませんでした。もう一度お試しいただくか、ほかの方法でログインしてください。",
    alreadyRegistered: "この端末はすでに登録されています。",
    unsupported: "この端末やブラウザでは、指紋・顔でのログインを使えません。ほかの方法でログインしてください。",
    failed: "うまくいきませんでした。しばらくしてから、もう一度お試しください。",
};

interface QuietConfig extends AxiosRequestConfig {
    handlerEnabled: boolean;
}

function quiet(): QuietConfig {
    return {handlerEnabled: false};
}

function fail(status: number, reason: string, message: string): AuthError {
    return {status, reason, message};
}

function ceremonyError(e: unknown): AuthError {
    switch (classifyCeremonyError(e)) {
        case "already_registered":
            return fail(0, CEREMONY_ALREADY_REGISTERED, PASSKEY_MESSAGES.alreadyRegistered);
        case "unsupported":
            return fail(0, CEREMONY_UNSUPPORTED, PASSKEY_MESSAGES.unsupported);
        default:
            return fail(0, CEREMONY_CANCELLED, PASSKEY_MESSAGES.cancelled);
    }
}

/** An http rejection -> AuthError (backend `message` shown as-is when it has a `reason`). */
function httpError(e: unknown): AuthError {
    return toAuthError(e, PASSKEY_MESSAGES.failed);
}

/** The user-facing message of a PasskeyService rejection (anything else -> fallback). */
export function passkeyErrorMessage(e: unknown, fallback: string): string {
    const err = (e && typeof e === "object" ? e : {}) as { reason?: unknown; message?: unknown };
    return typeof err.reason === "string" && typeof err.message === "string" && err.message ? err.message : fallback;
}

/** The `reason` of a PasskeyService rejection, or "". */
export function passkeyErrorReason(e: unknown): string {
    const err = (e && typeof e === "object" ? e : {}) as { reason?: unknown };
    return typeof err.reason === "string" ? err.reason : "";
}

/** Rejects with an AuthError; the screens show `.message`. */
export default class PasskeyService {

    public static isSupported(): boolean {
        return typeof window !== "undefined"
            && window.isSecureContext === true
            && typeof window.PublicKeyCredential === "function"
            && !!navigator.credentials;
    }

    /** A built-in fingerprint / face sensor (or PIN) is available on this device. */
    public static async hasPlatformAuthenticator(): Promise<boolean> {
        if (!PasskeyService.isSupported()) return false;
        try {
            return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        } catch (e) {
            return false;
        }
    }

    /** Sign in. On success the session cookie is set and the store has the user. */
    public static async login(): Promise<void> {
        if (!PasskeyService.isSupported()) throw fail(0, CEREMONY_UNSUPPORTED, PASSKEY_MESSAGES.unsupported);

        let opts: AuthenticationOptionsJson;
        try {
            const res = await httpResource.post("/auth/passkey/login/options", undefined, quiet());
            opts = res.data as AuthenticationOptionsJson;
        } catch (e) {
            throw httpError(e);
        }

        let cred: AssertionCredentialLike | null;
        try {
            const c = await navigator.credentials.get({publicKey: toRequestOptions(opts), mediation: "optional"} as CredentialRequestOptions);
            cred = c as unknown as AssertionCredentialLike | null;
        } catch (e) {
            throw ceremonyError(e);
        }
        if (!cred) throw ceremonyError(null);

        try {
            await httpResource.post("/auth/passkey/login/verify", loginBody(cred), quiet());
        } catch (e) {
            throw httpError(e);
        }
        await authService.authMe();
        const user = store.getters[AuthGetterTypes.getCurrentUser];
        if (!user || !(user.id || user.userId)) {
            // authMe() clears the user when /auth/me fails (cookie not kept).
            throw fail(0, "", "ログインを続けられませんでした。ブラウザの Cookie の設定を確認してください。");
        }
    }

    /** Register this device for the signed-in user. */
    public static async register(nickname: string): Promise<Passkey> {
        if (!PasskeyService.isSupported()) throw fail(0, CEREMONY_UNSUPPORTED, PASSKEY_MESSAGES.unsupported);

        let opts: RegistrationOptionsJson;
        try {
            const res = await httpResource.post("/passkey/registration/options", undefined, quiet());
            opts = res.data as RegistrationOptionsJson;
        } catch (e) {
            throw httpError(e);
        }

        let cred: AttestationCredentialLike | null;
        try {
            const c = await navigator.credentials.create({publicKey: toCreationOptions(opts)});
            cred = c as unknown as AttestationCredentialLike | null;
        } catch (e) {
            throw ceremonyError(e);
        }
        if (!cred) throw ceremonyError(null);

        let raw: unknown;
        try {
            const res = await httpResource.post("/passkey/registration/verify", registrationBody(cred, nickname), quiet());
            raw = res.data;
        } catch (e) {
            throw httpError(e);
        }
        const pk = toPasskey(raw);
        if (!pk) throw fail(0, "", "登録の結果を読み取れませんでした。一覧を開き直して確認してください。");
        return pk;
    }

    public static async list(): Promise<Passkey[]> {
        try {
            const res = await httpResource.get("/passkey/credentials", quiet());
            if (!Array.isArray(res.data)) return [];
            return (res.data as unknown[]).map(toPasskey).filter((p): p is Passkey => p !== null);
        } catch (e) {
            throw httpError(e);
        }
    }

    public static async remove(id: string): Promise<void> {
        try {
            await httpResource.delete(`/passkey/credentials/${encodeURIComponent(id)}`, quiet());
        } catch (e) {
            throw httpError(e);
        }
    }
}
